import axios from 'axios';
import { getRDFoxAuth, parseTSV, getRDFoxURL } from '../utils/rdfoxUtils';

// Obtenir les informations d'authentification
const RDFOX_AUTH = getRDFoxAuth();

/**
 * Vérifie la connexion au serveur RDFox
 */
const checkRDFoxConnection = async (): Promise<boolean> => {
  try {
    console.log(`Vérification de la connexion RDFox...`);
    const url = getRDFoxURL();
    const response = await axios.get(`${url}/server-info`, {
      auth: RDFOX_AUTH,
      headers: {
        'Accept': 'text/tab-separated-values'
      }
    });

    console.log(`Réponse de connexion RDFox:`, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers
    });

    if (response.status !== 200) {
      throw new Error(`Le serveur RDFox a renvoyé ${response.status}: ${response.statusText}`);
    }

    console.log('Informations du serveur RDFox:', response.data);
    return true;
  } catch (error) {
    console.error(`Impossible de se connecter à RDFox:`, error);
    return false;
  }
};

/**
 * Vérifie si un datastore existe
 */
const checkDatastoreExists = async (dataset: string): Promise<boolean> => {
  try {
    console.log(`Vérification de l'existence du datastore ${dataset}...`);
    const url = getRDFoxURL();
    const response = await axios.head(`${url}/datastores/${dataset}/info`, {
      auth: RDFOX_AUTH,
      headers: {
        'Accept': 'text/tab-separated-values'
      }
    });

    console.log(`Réponse de vérification du datastore ${dataset}:`, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers
    });

    return response.status === 200;
  } catch (error) {
    // Si l'erreur est 404, le datastore n'existe pas
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.log(`Le datastore ${dataset} n'existe pas`);
      return false;
    }
    
    console.error(`Erreur lors de la vérification du datastore ${dataset}:`, error);
    return false;
  }
};

/**
 * Crée un nouveau datastore
 */
const createDatastore = async (dataset: string): Promise<boolean> => {
  try {
    const exists = await checkDatastoreExists(dataset);
    if (exists) {
      console.log(`Le datastore ${dataset} existe déjà`);
      return true;
    }

    console.log(`Création du datastore ${dataset}...`);
    const url = getRDFoxURL();
    const response = await axios.put(`${url}/datastores/${dataset}`, null, {
      auth: RDFOX_AUTH,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    console.log(`Réponse de création du datastore ${dataset}:`, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers
    });

    if (response.status !== 201 && response.status !== 200) {
      throw new Error(`Échec de la création du datastore (${response.status}): ${response.statusText}`);
    }

    console.log(`Datastore ${dataset} créé avec succès`);
    return true;
  } catch (error) {
    console.error(`Échec de la création du datastore ${dataset}:`, error);
    return false;
  }
};

/**
 * Exécute une requête SPARQL sur un datastore RDFox
 */
const fetchRDFoxData = async (dataset: string, sparqlText: string) => {
  try {
    // Vérifier si RDFox est accessible
    const isConnected = await checkRDFoxConnection();
    if (!isConnected) {
      throw new Error(`Le serveur RDFox n'est pas accessible. Veuillez vérifier que RDFox est en cours d'exécution.`);
    }

    // Vérifier si la datastore existe
    const exists = await checkDatastoreExists(dataset);
    if (!exists) {
      const created = await createDatastore(dataset);
      if (!created) {
        throw new Error(`Impossible d'accéder ou de créer le datastore ${dataset}`);
      }
    }

    const url = getRDFoxURL();
    const fullUrl = `${url}/datastores/${dataset}/sparql`;
    console.log(`Envoi de la requête SPARQL à:`, fullUrl);
    console.log('Requête:', sparqlText);

    const response = await axios.post(fullUrl, sparqlText, {
      auth: RDFOX_AUTH,
      headers: {
        'Accept': 'text/tab-separated-values',
        'Content-Type': 'application/sparql-query'
      }
    });

    console.log(`Réponse SPARQL:`, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers
    });
    
    if (response.status !== 200) {
      console.error(`Erreur de requête RDFox (${response.status}):`, response.data);
      throw new Error(`Erreur de requête RDFox (${response.status}): ${response.statusText}`);
    }

    // Traiter la réponse TSV
    if (typeof response.data === 'string') {
      const parsedData = parseTSV(response.data);
      console.log(`Données de réponse RDFox (TSV parsé):`, parsedData);
      return parsedData;
    } 
    // Si la réponse est déjà au format JSON
    else if (typeof response.data === 'object') {
      console.log(`Données de réponse RDFox (JSON):`, response.data);
      return response.data;
    }
    
    throw new Error('Format de réponse RDFox non pris en charge');
  } catch (error) {
    console.error(`Erreur dans la requête RDFox:`, error);
    throw error;
  }
};

export default fetchRDFoxData;
export { checkRDFoxConnection, checkDatastoreExists, createDatastore };
